import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Article, ArticleContent } from '@/types'

import Link from 'next/link'
import { useMemo } from 'react'

import {
    CalendarDays,
    Clock,
    Eye,
    Link2,
    Lock,
    PencilLine,
    Printer,
    RefreshCcw,
    UserRound
} from 'lucide-react'

import { copyText } from '@/lib/clipboard'
import { formatJalali, formatNumber, toFaDigits } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageContainer } from '@/components/generals/page'
import { ConfluenceHint } from '@/components/generals/confluence-hint'
import { useToast } from '@/components/generals/providers'
import { Avatar } from '@/components/ds/avatar'
import { Breadcrumb } from '@/components/ds/breadcrumb'
import { Button, ButtonLink } from '@/components/ds/button'
import { cardStyles } from '@/components/ds/card'
import { Callout } from '@/components/ds/feedback'
import { SectionHeading } from '@/components/ds/section-heading'
import { ArticleCard } from '@/components/knowledge/article-card'
import { FavoriteButton } from '@/components/knowledge/favorite-button'
import { ArticleBody, buildToc } from '@/components/article/article-body'
import { ArticleHistory, ArticleProperties, TableOfContents } from '@/components/article/article-aside'
import { ArticleComments, ArticleFeedback, SuggestEdit } from '@/components/article/article-engagement'
import { ARTICLE_CONTENT } from '@/constants/article-content'
import { PERSON_BY_SLUG } from '@/constants/people'

import {
    CategoryBadge,
    ConfidentialityBadge,
    StatusBadge,
    TypeBadge
} from '@/components/knowledge/badges'

import {
    ARTICLES,
    ARTICLE_BY_SLUG,
    LISTED_ARTICLES
} from '@/constants/articles'

import { CATEGORY_BY_SLUG, subcategoryTitle } from '@/constants/taxonomy'

interface ArticlePageProps {
    slug: string
    content: ArticleContent
    related: string[]
}

interface StatusNoticeProps {
    article: Article
}

const StatusNotice: FC<StatusNoticeProps> = ({ article }): ReactNode => {
    const category = CATEGORY_BY_SLUG[article.category]
    const reviewer = PERSON_BY_SLUG[category.owner]
    const owner = PERSON_BY_SLUG[article.owner]

    if (article.status === 'in-review') {
        return (
            <div className='relative'>
                <ConfluenceHint id='article-review' />

                <Callout tone='warning' title='این دانش در انتظار بررسی است'>
                    <p>
                        پس از تأیید {reviewer?.name}، مالک حوزه {category.title}، منتشر می‌شود. تا آن زمان فقط شما و بازبین آن را می‌بینید.
                    </p>

                    <Link href='/me' className='mt-1 w-fit text-small font-semibold text-primary-strong hover:text-primary-deep'>
                        پیگیری در دانش‌های من
                    </Link>
                </Callout>
            </div>
        )
    }

    if (article.status === 'returned' && article.reviewNote) {
        const noteBy = PERSON_BY_SLUG[article.reviewNote.reviewer]

        return (
            <div className='relative'>
                <ConfluenceHint id='article-review' />

                <Callout tone='warning' title='بازبین درخواست اصلاح داده است'>
                    <blockquote className='border-s-2 border-warning ps-3 text-foreground'>
                        {article.reviewNote.text}
                    </blockquote>

                    <p className='text-small text-muted-foreground'>
                        {noteBy?.name} · {formatJalali(article.reviewNote.date)}
                    </p>

                    <div className='mt-2 flex flex-wrap gap-2'>
                        <ButtonLink href={`/new?edit=${article.slug}`} size='sm'>
                            <PencilLine aria-hidden='true' />
                            اعمال اصلاحات
                        </ButtonLink>

                        <ButtonLink href='/me' size='sm' variant='outline'>
                            مشاهده در دانش‌های من
                        </ButtonLink>
                    </div>
                </Callout>
            </div>
        )
    }

    if (article.status === 'needs-update') {
        return (
            <div className='relative'>
                <ConfluenceHint id='article-review' />

                <Callout tone='warning' title='این دانش در حال بازبینی است'>
                    <p>
                        مالک دانش، {owner?.name}، در حال به‌روزرسانی بخشی از اطلاعات این صفحه است. پیش از استفاده، به تاریخ آخرین به‌روزرسانی توجه کنید.
                    </p>
                </Callout>
            </div>
        )
    }

    return null
}

const ArticlePage: FC<ArticlePageProps> = ({ slug, content, related }): ReactNode => {
    const article = ARTICLE_BY_SLUG[slug]
    const { notify } = useToast()
    const toc = useMemo(() => buildToc(article, content), [article, content])
    const category = CATEGORY_BY_SLUG[article.category]
    const author = PERSON_BY_SLUG[article.author]
    const owner = PERSON_BY_SLUG[article.owner]
    const version = content.history[0]?.version ?? 1
    const relatedArticles = related.map(item => ARTICLE_BY_SLUG[item]).filter(Boolean)

    const share = async () => {
        const copied = await copyText(window.location.href.split('#')[0])

        notify(copied ? 'پیوند این دانش کپی شد' : 'کپی پیوند ممکن نشد', copied ? 'success' : 'error')
    }

    const meta = [
        {
            label: 'نویسنده',
            value: author ? (
                <Link href={`/people/${author.slug}`} className='inline-flex items-center gap-2 hover:text-primary-deep'>
                    <Avatar name={author.name} tone={author.tone} size='xs' />
                    {author.name}
                </Link>
            ) : '-'
        },
        {
            label: 'مالک دانش',
            value: owner ? (
                <Link href={`/people/${owner.slug}`} className='inline-flex items-center gap-1.5 hover:text-primary-deep'>
                    <UserRound className='size-4 text-subtle' aria-hidden='true' />
                    {owner.name}
                </Link>
            ) : '-'
        },
        {
            label: 'تاریخ انتشار',
            value: (
                <span className='inline-flex items-center gap-1.5'>
                    <CalendarDays className='size-4 text-subtle' aria-hidden='true' />
                    {formatJalali(article.publishedAt)}
                </span>
            )
        },
        {
            label: 'آخرین به‌روزرسانی',
            value: (
                <span className='inline-flex items-center gap-1.5'>
                    <RefreshCcw className='size-4 text-subtle' aria-hidden='true' />
                    {formatJalali(article.updatedAt)}
                </span>
            )
        },
        {
            label: 'زمان مطالعه',
            value: (
                <span className='inline-flex items-center gap-1.5'>
                    <Clock className='size-4 text-subtle' aria-hidden='true' />
                    {toFaDigits(article.readMinutes)} دقیقه
                </span>
            )
        },
        {
            label: 'بازدید',
            value: (
                <span className='inline-flex items-center gap-1.5'>
                    <Eye className='size-4 text-subtle' aria-hidden='true' />
                    {article.views > 0 ? formatNumber(article.views) : 'هنوز منتشر نشده'}
                </span>
            )
        }
    ]

    return (
        <Layout>
            <Seo title={article.title} description={article.summary} />

            <PageContainer key={slug} className='flex flex-col gap-6'>
                <Breadcrumb
                    items={[
                        { label: category.title, href: `/categories/${category.slug}` },
                        { label: subcategoryTitle(article.category, article.subcategory), href: `/categories/${category.slug}?sub=${article.subcategory}` },
                        { label: article.title }
                    ]}
                />

                <StatusNotice article={article} />

                <header className='flex flex-col gap-5'>
                    <div className='flex flex-wrap items-center gap-2'>
                        <CategoryBadge category={article.category} />
                        <TypeBadge type={article.type} />
                        <StatusBadge status={article.status} />
                        <ConfidentialityBadge level={article.confidentiality} />
                    </div>

                    <h1 className='max-w-4xl text-[1.625rem] font-extrabold leading-[1.55] text-navy sm:text-h1'>
                        {article.title}
                    </h1>

                    <div className='relative'>
                        <ConfluenceHint id='article-meta' />

                        <dl className={cardStyles(false, 'grid grid-cols-2 gap-x-4 gap-y-4 p-4 sm:grid-cols-3 xl:grid-cols-6')}>
                            {meta.map(item => (
                                <div key={item.label} className='flex min-w-0 flex-col gap-1'>
                                    <dt className='text-caption text-muted-foreground'>{item.label}</dt>
                                    <dd className='truncate text-small font-semibold text-navy'>{item.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>

                    <div className='relative flex flex-wrap items-center gap-2'>
                        <ConfluenceHint id='article-actions' />

                        <FavoriteButton slug={article.slug} />

                        <Button variant='outline' onClick={share}>
                            <Link2 aria-hidden='true' />
                            اشتراک‌گذاری پیوند
                        </Button>

                        <ButtonLink href='#suggest' variant='ghost'>
                            <PencilLine aria-hidden='true' />
                            پیشنهاد اصلاح
                        </ButtonLink>

                        <Button variant='ghost' size='icon' onClick={() => window.print()} aria-label='چاپ یا ذخیره به‌صورت PDF' title='چاپ یا ذخیره به‌صورت PDF'>
                            <Printer aria-hidden='true' />
                        </Button>
                    </div>
                </header>

                {article.confidentiality === 'confidential' && (
                    <Callout tone='note' title='محتوای محرمانه'>
                        <p className='flex items-start gap-2'>
                            <Lock className='mt-1 size-4 shrink-0 text-navy' aria-hidden='true' />
                            این دانش فقط برای افراد و گروه‌های مجاز نمایش داده می‌شود. از اشتراک‌گذاری آن خارج از سامانه خودداری کنید.
                        </p>
                    </Callout>
                )}

                <div className='grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-8'>
                    <div className='flex min-w-0 flex-col gap-6'>
                        <div className='lg:hidden'>
                            <TableOfContents entries={toc} variant='compact' />
                        </div>

                        <article aria-label={article.title} className={cardStyles(false, 'p-5 sm:p-8')}>
                            <ArticleBody article={article} content={content} />
                        </article>

                        <ArticleFeedback article={article} />
                        <SuggestEdit article={article} sections={toc.map(entry => entry.title)} />
                        <ArticleComments initial={content.comments} />
                    </div>

                    <aside aria-label='اطلاعات تکمیلی' className='flex flex-col gap-5'>
                        <ArticleProperties article={article} version={version} />
                        <ArticleHistory history={content.history} />

                        <div className='hidden lg:sticky lg:top-24 lg:block'>
                            <TableOfContents entries={toc} />
                        </div>
                    </aside>
                </div>

                {relatedArticles.length > 0 && (
                    <section id='related' aria-labelledby='related-title' className='relative mt-4 flex flex-col gap-5'>
                        <ConfluenceHint id='article-related' />

                        <SectionHeading
                            id='related-title'
                            title='دانش‌های مرتبط'
                            description='دانش‌هایی با برچسب‌ها و حوزه مشترک که ممکن است برای شما مفید باشد.'
                            action={{ label: 'همه دانش‌های این حوزه', href: `/categories/${category.slug}` }}
                        />

                        <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-3'>
                            {relatedArticles.map(item => (
                                <ArticleCard key={item.slug} article={item} />
                            ))}
                        </div>
                    </section>
                )}
            </PageContainer>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: ARTICLES.filter(article => ARTICLE_CONTENT[article.slug]).map(article => ({ params: { slug: article.slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<ArticlePageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const article = ARTICLE_BY_SLUG[slug]
    const content = ARTICLE_CONTENT[slug]

    if (!article || !content) return { notFound: true }

    const related = LISTED_ARTICLES
        .filter(other => other.slug !== slug)
        .map(other => ({
            slug: other.slug,
            views: other.views,
            score: other.labels.filter(label => article.labels.includes(label)).length * 2
                + (other.category === article.category ? 1 : 0)
                + (other.type === article.type ? 1 : 0)
        }))
        .filter(item => item.score > 1)
        .sort((a, b) => b.score - a.score || b.views - a.views)
        .slice(0, 3)
        .map(item => item.slug)

    return { props: { slug, content, related } }
}

export default ArticlePage
